// src/components/dashboard/RegistrationsByGroup.tsx
import React from "react";
import {
    Typography,
    Box,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    CircularProgress,
    Alert,
    Paper
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import GroupIcon from "@mui/icons-material/Group";
import { useRegistrations } from "../../hooks/useRegistrations";

interface RegistrationsByGroupProps {
    competitionId: string;
    competitionName?: string;
}

// Helper function to determine chip color based on count
const getChipColor = (count: number): "error" | "warning" | "success" => {
  if (count >= 8) return "success";
  if (count >= 4) return "warning";
  return "error";
};

const columnWidths = {
    name: '30%',
    gender: '20%',
    year: '20%',
    club: '30%'
};

const RegistrationsByGroup: React.FC<RegistrationsByGroupProps> = ({ competitionId, competitionName }) => {
    const isFilderPokal = competitionName?.includes("Filder Pokal") ?? false;
    const { registrations, loading, error } = useRegistrations(competitionId, {
        sortByCompetitionGroup: true
      });

    const getGenderLabel = (gender: string) => {
        switch (gender) {
            case 'm':
                return 'männlich';
            case 'w':
                return 'weiblich';
            case 'mixed':
                return 'gemischt';
            default:
                return gender;
        }
    };

    // Calculate summary totals
    const calculateSummary = () => {
        const totalElite = registrations
            .filter(entry => entry.type.includes("Elite"))
            .reduce((sum, entry) => sum + entry.count, 0);
        
        const totalBasis = registrations
            .filter(entry => entry.type.includes("Basis"))
            .reduce((sum, entry) => sum + entry.count, 0);
        
        // First, we need to count TB Ruit athletes in each registration group
        const tbRuitCounts = registrations.map(group => {
            // Filter athletes in this group who belong to TB Ruit
            const tbRuitAthletes = group.athletes.filter(athlete => 
                athlete.club && (
                    (typeof athlete.club === 'string' && athlete.club.includes("TB Ruit")) ||
                    (athlete.club && athlete.club.includes("TB Ruit"))
                )
            );
            
            return {
                type: group.type,
                count: tbRuitAthletes.length
            };
        });
        
        // Now calculate totals for TB Ruit
        const tbRuitElite = tbRuitCounts
            .filter(entry => entry.type.includes("Elite"))
            .reduce((sum, entry) => sum + entry.count, 0);
        
        const tbRuitBasis = tbRuitCounts
            .filter(entry => entry.type.includes("Basis"))
            .reduce((sum, entry) => sum + entry.count, 0);

        return {
            elite: totalElite,
            basis: totalBasis,
            total: totalElite + totalBasis,
            tbrElite: tbRuitElite,
            tbrBasis: tbRuitBasis,
            tbrTotal: tbRuitElite + tbRuitBasis
        };
    };

    // Gemeinsame Tabellenköpfe-Komponente
    const TableHeaders = () => (
        <TableHead>
            <TableRow>
                <TableCell width={columnWidths.name}>Name</TableCell>
                <TableCell width={columnWidths.gender}>Geschlecht</TableCell>
                <TableCell width={columnWidths.year}>Jahrgang</TableCell>
                <TableCell width={columnWidths.club}>Verein</TableCell>
            </TableRow>
        </TableHead>
    );

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }

    if (!registrations.length) {
        return (
            <Alert severity="info">
                Keine Meldungen für diesen Wettkampf vorhanden.
            </Alert>
        );
    }

    const summary = calculateSummary();

    return (
        <Box sx={{ p: 3 }}>
            {/* Summary component */}
            <Paper elevation={2} sx={{ mb: 3, p: 2 }}>
                <Typography variant="h6" gutterBottom>
                    Gesamtübersicht
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Chip 
                        label={`Gesamt: ${summary.total} Teilnehmer`} 
                        color="primary"
                        sx={{ fontWeight: 'bold' }}
                    />
                    <Chip 
                        label={`Elite: ${summary.elite} Teilnehmer`} 
                        color="info"
                    />
                    <Chip 
                        label={`Basis: ${summary.basis} Teilnehmer`} 
                        color="default"
                    />
                    {isFilderPokal && <>
                        <Chip
                            label={`TBR-Gesamt: ${summary.tbrTotal} Teilnehmer`}
                            color="primary"
                            sx={{ fontWeight: 'bold' }}
                        />
                        <Chip
                            label={`TBR-Elite: ${summary.tbrElite} Teilnehmer`}
                            color="info"
                        />
                        <Chip
                            label={`TBR-Basis: ${summary.tbrBasis} Teilnehmer`}
                            color="default"
                        />
                    </>}
                </Box>
            </Paper>

            <Typography variant="h6" gutterBottom>
                Wettkampf-Meldungen nach Klassen
            </Typography>
            {registrations.map((entry) => (
                <Accordion 
                    key={entry.competitionGroup} 
                    sx={{ 
                        mb: 1,
                        '& .MuiAccordionDetails-root': {
                            padding: 0
                        }
                    }}
                >
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            },
                        }}
                    >
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between',
                            width: '100%',
                            pr: 2 
                        }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                                {entry.competitionGroup}
                            </Typography>
                            <Chip
                                icon={<GroupIcon />}
                                label={`${entry.count} Teilnehmer`}
                                size="small"
                                color={getChipColor(entry.count)}
                            />
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                        <TableContainer>
                            <Table size="small" sx={{ tableLayout: 'fixed' }}>
                                <TableHeaders />
                                <TableBody>
                                    {entry.athletes.map((athlete) => (
                                        <TableRow 
                                            key={athlete._id}
                                            sx={{ 
                                                '&:last-child td, &:last-child th': { border: 0 },
                                                '& td': { 
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }
                                            }}
                                        >
                                            <TableCell width={columnWidths.name}>
                                                {athlete.firstname} {athlete.surname}
                                            </TableCell>
                                            <TableCell width={columnWidths.gender}>
                                                {getGenderLabel(athlete.gender)}
                                            </TableCell>
                                            <TableCell width={columnWidths.year}>
                                                {athlete.age}
                                            </TableCell>
                                            <TableCell width={columnWidths.club}>
                                                {athlete.club}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </AccordionDetails>
                </Accordion>
            ))}
        </Box>
    );
};

export default RegistrationsByGroup;